import { Link } from 'react-router-dom'
import { SITE } from '@shared/config'
import { getAllPosts } from '@shared/lib/posts'
import { useSeo } from '@shared/lib/seo'
import { Header } from '@widgets/header'
import { Footer } from '@widgets/footer'
import * as styles from './AuthorPage.css'

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export function AuthorPage() {
  const posts = getAllPosts().filter((post) => post.author === SITE.author.name)
  const latestPosts = posts.slice(0, 12)

  useSeo({
    title: `${SITE.author.name} | ${SITE.name}`,
    description: SITE.author.description,
    path: SITE.author.route,
    image: SITE.author.image,
    type: 'website',
  })

  return (
    <div className={styles.page}>
      <Header />
      <main>
        <section className={styles.hero}>
          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <Link className={styles.breadcrumbLink} to="/">Home</Link>
            <span aria-hidden="true">›</span>
            <span>{SITE.author.name}</span>
          </nav>
          <div className={styles.profile}>
            <img
              className={styles.portrait}
              src={SITE.author.image}
              alt={`${SITE.author.name}, editor and author at ${SITE.name}`}
              width="240"
              height="240"
            />
            <div>
              <p className={styles.eyebrow}>Editor and author</p>
              <h1 className={styles.title}>{SITE.author.name}</h1>
              <p className={styles.role}>{SITE.author.jobTitle}</p>
              <p className={styles.description}>{SITE.author.description}</p>
            </div>
          </div>
        </section>

        <section className={styles.credentials} aria-labelledby="credentials-heading">
          <div>
            <p className={styles.sectionLabel}>Background</p>
            <h2 id="credentials-heading" className={styles.sectionTitle}>Horticulture education and practice</h2>
          </div>
          <dl className={styles.credentialList}>
            <div className={styles.credentialItem}>
              <dt className={styles.credentialTerm}>Current work</dt>
              <dd className={styles.credentialDescription}>{SITE.author.currentWork}</dd>
            </div>
            {SITE.author.education.map((education) => (
              <div className={styles.credentialItem} key={education.degree}>
                <dt className={styles.credentialTerm}>{education.degree}</dt>
                <dd className={styles.credentialDescription}>
                  {'focus' in education ? `${education.focus} — ` : ''}
                  {education.institution}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={styles.latest} aria-labelledby="latest-guides-heading">
          <div className={styles.latestHeading}>
            <div>
              <p className={styles.sectionLabel}>Published work</p>
              <h2 id="latest-guides-heading" className={styles.sectionTitle}>Latest guides</h2>
            </div>
            <p className={styles.postCount}>{posts.length} guides</p>
          </div>
          <ul className={styles.postList}>
            {latestPosts.map((post) => (
              <li key={post.slug}>
                <Link className={styles.postLink} to={post.route}>
                  <span className={styles.postLinkText}>
                    <span className={styles.postCategory}>{post.category}</span>
                    <span className={styles.postTitle}>{post.title}</span>
                  </span>
                  <time className={styles.postDate} dateTime={post.date}>{formatDate(post.date)}</time>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <Footer />
    </div>
  )
}
